import * as React from "react";
import {
	Box,
	Avatar,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Stack,
	Typography,
} from "@mui/material";
import { useTheme, useMediaQuery } from "@mui/material";
import {
	fetchFavorites,
	removeFavorite,
	updateFavorite,
	type FavoriteItem,
} from "./api/favorites";

// Basic page listing user's favorite products by ID.
// The backend currently returns product IDs only; name/price lookup can be added later.
export default function MyFavoritesPage() {
	const theme = useTheme();
	const isXs = useMediaQuery(theme.breakpoints.down("sm"));

	const [items, setItems] = React.useState<FavoriteItem[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [busyIds, setBusyIds] = React.useState<number[]>([]);
	const [pendingDelete, setPendingDelete] = React.useState<FavoriteItem | null>(
		null,
	);

	const setBusy = React.useCallback((id: number, busy: boolean) => {
		setBusyIds((prev) => {
			if (busy) {
				return prev.includes(id) ? prev : [...prev, id];
			}
			return prev.filter((item) => item !== id);
		});
	}, []);

	React.useEffect(() => {
		let cancelled = false;
		const run = async () => {
			setLoading(true);
			try {
				const data = await fetchFavorites();
				if (!cancelled) setItems(data);
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error("Failed to load favorites", e);
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		void run();
		return () => {
			cancelled = true;
		};
	}, []);

	const handleRemove = React.useCallback(
		async (fav: FavoriteItem) => {
			setBusy(fav.id, true);
			try {
				await removeFavorite(fav.productId);
				setItems((prev) => prev.filter((item) => item.id !== fav.id));
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error("Failed to remove favorite", e);
			} finally {
				setBusy(fav.id, false);
			}
		},
		[setBusy],
	);

	const handleRequestRemove = React.useCallback((fav: FavoriteItem) => {
		setPendingDelete(fav);
	}, []);

	const handleCloseDelete = React.useCallback(() => {
		setPendingDelete(null);
	}, []);

	const handleConfirmRemove = React.useCallback(async () => {
		if (!pendingDelete) return;
		await handleRemove(pendingDelete);
		setPendingDelete(null);
	}, [handleRemove, pendingDelete]);

	const handleToggleAlerts = React.useCallback(
		async (fav: FavoriteItem) => {
			const nextActive = !fav.isActive;
			setBusy(fav.id, true);
			try {
				await updateFavorite(fav.productId, nextActive);
				setItems((prev) =>
					prev.map((item) =>
						item.id === fav.id
							? { ...item, isActive: nextActive }
							: item,
					),
				);
			} catch (e) {
				// eslint-disable-next-line no-console
				console.error("Failed to update favorite alerts", e);
			} finally {
				setBusy(fav.id, false);
			}
		},
		[setBusy],
	);

	const confirmBusy = pendingDelete
		? busyIds.includes(pendingDelete.id)
		: false;

	return (
		<Box>
			<Typography
				variant={isXs ? "h5" : "h4"}
				fontWeight={800}
				sx={{ mb: 2 }}
			>
				My Favorites
			</Typography>

			<Card variant="outlined">
				<CardContent sx={{ p: { xs: 2, sm: 3 } }}>
					<Typography variant="subtitle1" fontWeight={600} gutterBottom>
						Favorite products
					</Typography>
	<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
		This view shows the product names you have favorited.
	</Typography>

					{loading ? (
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								minHeight: 160,
							}}
						>
							<CircularProgress size={24} />
						</Box>
					) : items.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							You have not added any favorites yet.
						</Typography>
					) : (
						<List dense>
							{items.map((fav) => {
								const createdText = fav.createdAt
									? new Date(fav.createdAt).toLocaleString()
									: "";
								const statusText = fav.isActive
									? "Alerts on"
									: "Alerts off";
								const secondaryParts = [];
								if (createdText) {
									secondaryParts.push(`Added at ${createdText}`);
								}
								secondaryParts.push(statusText);
								const secondaryText = secondaryParts.join(" • ");
								const isBusy = busyIds.includes(fav.id);
								return (
									<ListItem
										key={fav.id}
										disableGutters
										sx={{ gap: 1.5, alignItems: "flex-start", py: 1 }}
									>
										<ListItemAvatar sx={{ minWidth: 0 }}>
											<Avatar
												variant="rounded"
												src={fav.imageUrl ?? undefined}
												alt={fav.productName}
												sx={{
													width: 40,
													height: 40,
													fontSize: 14,
													bgcolor: "action.hover",
												}}
											>
												{fav.productName?.[0]?.toUpperCase() ?? "?"}
											</Avatar>
										</ListItemAvatar>
										<ListItemText
											primary={
												fav.productName || `Product ${fav.productId}`
											}
											secondary={secondaryText}
											sx={{ flex: 1, minWidth: 0 }}
										/>
										<Stack
											direction={{ xs: "column", sm: "row" }}
											spacing={0.5}
											sx={{ flexShrink: 0 }}
										>
											<Button
												size="small"
												variant="contained"
												color={fav.isActive ? "warning" : "success"}
												onClick={() => void handleToggleAlerts(fav)}
												disabled={isBusy}
											>
												{fav.isActive ? "Disable alerts" : "Enable alerts"}
											</Button>
											<Button
												size="small"
												variant="text"
												color="error"
												sx={{ textTransform: "uppercase" }}
												onClick={() => handleRequestRemove(fav)}
												disabled={isBusy}
											>
												Delete
											</Button>
										</Stack>
									</ListItem>
								);
							})}
						</List>
					)}
				</CardContent>
			</Card>
			<Dialog
				open={Boolean(pendingDelete)}
				onClose={confirmBusy ? undefined : handleCloseDelete}
				aria-labelledby="confirm-delete-title"
				aria-describedby="confirm-delete-description"
			>
				<DialogTitle id="confirm-delete-title">
					Remove favorite?
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="confirm-delete-description">
						This will remove the item from your favorites list. You can add
						it again later if needed.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDelete} disabled={confirmBusy}>
						Cancel
					</Button>
					<Button
						onClick={() => void handleConfirmRemove()}
						color="error"
						variant="contained"
						disabled={confirmBusy}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
